import { useT } from "@transifex/react";
import { ShareModes } from "../../../enums/shareFlow";
import type { ClassActivityCategory } from "../../../constants/ClassActivityConstants";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import ShareOptionCard from "../ShareOptionCard";

interface ShareModeStepProps {
    selectedMode: ShareModes | null;
    onSelectMode: (shareMode: ShareModes) => void;
    isTestDisabled: boolean;
    // Distinguishes the two reasons TEST is unavailable: a public class is the wrong kind of class,
    // whereas "no class" is none at all — the private-class hint alone reads as a non-sequitur there.
    isNoClassSelected: boolean;
    testName: string;
    classActivityCategory: ClassActivityCategory;
}

const ShareModeStep = (props: ShareModeStepProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t_question = t('How would you like to share "{testName}"?', {
        testName: props.testName,
        _tags: "ShareFlow, ShareModeStep",
    });
    const t_testTitle = t("As a test", { _tags: "ShareFlow, ShareModeStep" });
    const t_testSubtitle = t("(Once only)", { _tags: "ShareFlow, ShareModeStep" });
    const t_testDescription = t("I want to see the answers and give feedback.", {
        _tags: "ShareFlow, ShareModeStep",
    });
    const t_practiceTitle = t("For practice", { _tags: "ShareFlow, ShareModeStep" });
    const t_practiceSubtitle = t("(Unlimited repeats)", { _tags: "ShareFlow, ShareModeStep" });
    const t_practiceDescription = t("My students' answers will not be saved.", {
        _tags: "ShareFlow, ShareModeStep",
    });
    const t_testOnlyPrivate = t("Tests are only possible in private classes.", {
        _tags: "ShareFlow, ShareClassStep, ShareModeStep",
    });
    const t_testNeedsClass = t("A test needs a private class. Select one to share as a test.", {
        _tags: "ShareFlow, ShareModeStep",
    });
    const t_hint = t("Note: After sharing, the task can no longer be modified.", {
        _tags: "ShareFlow, ShareModeStep",
    });

    const modeCards = [
        {
            shareMode: ShareModes.TEST,
            title: t_testTitle,
            subtitle: t_testSubtitle,
            description: t_testDescription,
            isDisabled: props.isTestDisabled,
        },
        {
            shareMode: ShareModes.PRACTICE,
            title: t_practiceTitle,
            subtitle: t_practiceSubtitle,
            description: t_practiceDescription,
            isDisabled: false,
        },
    ];

    const renderModeCards = () =>
        modeCards.map((card) => (
            <ShareOptionCard
                key={card.shareMode}
                data-testid={`share-mode-option-${card.shareMode}`}
                isSelected={props.selectedMode === card.shareMode}
                isDisabled={card.isDisabled}
                classActivityCategory={props.classActivityCategory}
                onClick={() => props.onSelectMode(card.shareMode)}
                className="flex flex-col items-center min-h-[180px]"
            >
                <div className="flex flex-col items-center border-b border-p6-grey-light pb-s w-full gap-xs">
                    <p className="text-xl font-semibold text-p6-grey-dark">{card.title}</p>
                    <p className="text-m text-p6-grey-dark">{card.subtitle}</p>
                </div>
                <div className="flex items-center justify-center w-full flex-1 pt-s">
                    <p className="text-l text-p6-grey-dark">{card.description}</p>
                </div>
            </ShareOptionCard>
        ));

    const renderInfoLine = (text: string) => (
        <div className="flex gap-xs items-center">
            <PhaseSixIcon
                name="info"
                className="text-[30px] text-p6-grey-dark"
            />
            <p className="text-l text-p6-grey-dark">{text}</p>
        </div>
    );

    return (
        <div className="flex flex-col gap-s w-full">
            <p className="text-l text-p6-grey-dark">{t_question}</p>

            <div className="flex flex-col gap-xs">
                <div className={`grid gap-m ${getStyleBasedOnBreakpoint(Breakpoint.SM, "grid-cols-2", "grid-cols-1")}`}>
                    {renderModeCards()}
                </div>

                {props.isTestDisabled && renderInfoLine(props.isNoClassSelected ? t_testNeedsClass : t_testOnlyPrivate)}
                {renderInfoLine(t_hint)}
            </div>
        </div>
    );
};

export default ShareModeStep;
